import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from './UserContext';
import './navbar.css';
import { useTranslation } from './LanguageContext';
import logoFECAnalytics from '../img/FECAnalytics.png';

export default function NavBar({ onAnalyseClick, onLoginClick }) {
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const { t, lang, setLang } = useTranslation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileTriggerRef = useRef(null);

  const alias = {
    features: 'services',
    contact: 'contact',
    home: 'home',
    about: 'about',
    Demo: 'demo',
  };

  const navItems = [
    {
      id: 'home',
      href: '#home',
      label: t('nav.home'),
      icon: <svg className="nav-link-icon" viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
    },
    {
      id: 'features',
      href: '#features',
      label: t('nav.features'),
      icon: <svg className="nav-link-icon" viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
    },
    {
      id: 'asavoir',
      href: '#asavoir',
      label: t('nav.asavoir'),
      icon: <svg className="nav-link-icon" viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
    },
    {
      id: 'contact',
      href: '#contact',
      label: t('nav.contact'),
      icon: <svg className="nav-link-icon" viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
    },
  ];

  const brandParts = t('brand.name').split(' ');
  const brandPrimary = brandParts[0] || t('brand.name');
  const brandSecondary = brandParts.slice(1).join(' ');
  const mobileMenuLabel =
    lang === 'fr'
      ? mobileMenuOpen
        ? 'Fermer le menu de navigation'
        : 'Ouvrir le menu de navigation'
      : mobileMenuOpen
        ? 'Close navigation menu'
        : 'Open navigation menu';

  const toId = (raw) => alias[raw] || raw;

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMenuOpen(false);
    setMobileMenuOpen((prev) => !prev);
  };

  const goHome = (e) => {
    e.preventDefault();
    navigate('/', { state: { scrollTo: 'home' } });
  };

  const goSection = (e, id) => {
    e.preventDefault();
    const sectionId = toId(id);
    navigate('/', { state: { scrollTo: sectionId } });
  };

  const goLogin = (e) => {
    e.preventDefault();
    if (typeof onLoginClick === 'function') {
      try {
        onLoginClick();
      } catch (err) {
        console.error(err);
      }
    } else {
      navigate('/login', { state: { from: location.pathname, scrollTo: 'home' } });
    }
  };

  const handleNavAction = (action) => (e) => {
    closeMobileMenu();
    action(e);
  };

  const handleHistoryNavigation = (e) => {
    e.preventDefault();
    closeMobileMenu();
    navigate('/historique');
  };

  const handleLogout = async (e) => {
    e?.preventDefault?.();
    const url = '/api/logout';

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('Erreur logout:', response.statusText);
        return;
      }

      const data = await response.json();
      console.log(data.message);

      setUser(null);
      closeMobileMenu();
      navigate('/', { replace: true, state: { scrollTo: 'home' } });
    } catch (error) {
      console.error('Erreur fetch:', error);
    }
  };

  useEffect(() => {
    if (!menuOpen) return;

    const onClickOutside = (ev) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(ev.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(ev.target)
      ) {
        setMenuOpen(false);
      }
    };

    const onKey = (ev) => {
      if (ev.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const getFocusableElements = () => {
      if (!mobileMenuRef.current) return [];

      return Array.from(
        mobileMenuRef.current.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => {
        if (!(element instanceof HTMLElement)) return false;
        return !element.hasAttribute('disabled') && element.offsetParent !== null;
      });
    };

    const onClickOutside = (ev) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(ev.target) &&
        mobileTriggerRef.current &&
        !mobileTriggerRef.current.contains(ev.target)
      ) {
        closeMobileMenu();
      }
    };

    const onKeyDown = (ev) => {
      if (ev.key === 'Escape') {
        closeMobileMenu();
        return;
      }

      if (ev.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (ev.shiftKey && document.activeElement === firstElement) {
        ev.preventDefault();
        lastElement.focus();
      } else if (!ev.shiftKey && document.activeElement === lastElement) {
        ev.preventDefault();
        firstElement.focus();
      }
    };

    const onResize = () => {
      if (window.innerWidth >= 1025) {
        closeMobileMenu();
      }
    };

    const previousOverflow = document.body.style.overflow;
    const focusableElements = getFocusableElements();

    document.body.style.overflow = 'hidden';
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, [mobileMenuOpen]);

  return (
    <header>
      <nav className="navbar">
        <div className="container navbar-inner">
          <div className="navbar-brand">
            <a href="#home" className="logo" onClick={handleNavAction(goHome)}>
              <img src={logoFECAnalytics} alt="FECAnalytics" width={150} />
            </a>
          </div>

          <button
            ref={mobileTriggerRef}
            className={`mobile-menu-btn ${mobileMenuOpen ? 'is-open' : ''}`}
            type="button"
            aria-controls="navbar-menu"
            aria-expanded={mobileMenuOpen ? 'true' : 'false'}
            aria-label={mobileMenuLabel}
            onClick={toggleMobileMenu}
          >
            <span className="mobile-menu-btn__line" aria-hidden="true"></span>
            <span className="mobile-menu-btn__line" aria-hidden="true"></span>
            <span className="mobile-menu-btn__line" aria-hidden="true"></span>
          </button>

          <button
            type="button"
            className={`mobile-menu-overlay ${mobileMenuOpen ? 'is-open' : ''}`}
            aria-label={lang === 'fr' ? 'Fermer le menu' : 'Close menu'}
            tabIndex={mobileMenuOpen ? 0 : -1}
            onClick={closeMobileMenu}
          ></button>

          <div
            id="navbar-menu"
            ref={mobileMenuRef}
            className={`navbar-menu ${mobileMenuOpen ? 'is-open' : ''}`}
          >
            {/* <div className="mobile-menu-header">
              <img src={logoFECAnalytics} alt="FECAnalytics" width={110} />
            </div> */}

            <div className="auth-nav-links">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  onClick={handleNavAction((e) => goSection(e, item.id))}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </a>
              ))}
              <a
                href="/documentation"
                onClick={handleNavAction((e) => {
                  e.preventDefault();
                  navigate('/documentation');
                })}
              >
                <svg className="nav-link-icon" viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                <span>{t('nav.documentation')}</span>
              </a>
            </div>

            <div className="nav-links">
              <div className="auth-bar">
                <div className="auth-actions">
                  <select
                    className="lang-toggle"
                    value={lang}
                    onChange={(e) => setLang(e.target.value)}
                    title={t('lang.toggle')}
                    aria-label={t('lang.toggle')}
                  >
                    <option value="fr">FR</option>
                    <option value="en">EN</option>
                    <option value="ar">AR</option>
                  </select>

                  {!user && (
                    <a href="#login" className="login-btn" onClick={handleNavAction(goLogin)}>
                      {t('nav.login')}
                    </a>
                  )}

                  {user && (
                    <div className="user-menu">
                      <button
                        ref={triggerRef}
                        type="button"
                        className="user-menu-trigger"
                        onClick={() => setMenuOpen((value) => !value)}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen ? 'true' : 'false'}
                        title={user?.username || 'Utilisateur'}
                      >
                        <img
                          className="auth-avatar"
                          src="/icons/utilisateur.png"
                          alt="Utilisateur"
                        />
                        <span className="user-menu-name">{user.username}</span>
                        {user.subscription !== undefined && (
                          <span
                            className={`user-credit-badge ${user.subscription?.active ? 'is-active' : 'is-inactive'
                              }`}
                          >
                            {user.subscription?.nbr_files || 0} cred.
                          </span>
                        )}
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          className={`user-menu-caret ${menuOpen ? 'is-open' : ''}`}
                        >
                          <path d="M7 10l5 5 5-5H7z"></path>
                        </svg>
                      </button>

                      {menuOpen && (
                        <div
                          ref={menuRef}
                          role="menu"
                          aria-label="Menu utilisateur"
                          className="user-menu-dropdown"
                        >
                          <a
                            href="/historique"
                            onClick={handleHistoryNavigation}
                            role="menuitem"
                            className="user-menu-item"
                          >
                            Historique
                          </a>
                          <button
                            onClick={handleLogout}
                            role="menuitem"
                            className="user-menu-item user-menu-item-button"
                          >
                            {t('nav.logout')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {!user && (
                  <img
                    className="auth-avatar auth-avatar-guest"
                    src="/icons/utilisateur.png"
                    alt="Utilisateur"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}

NavBar.defaultProps = {
  onLoginClick: null,
  onAnalyseClick: null,
};
